/**
 * Bulk import dialog for IOCs. Accepts either CSV (one per line:
 * `kind,value,severity,source?,reference?,note?`) or JSON array of
 * IocInput objects. Sends to `/admin/iocs/bulk` and renders the
 * per-row pass/fail list.
 */
import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Check, X } from "lucide-react";

import { useBulkIocs } from "@/hooks/security/useIocs";
import type { CreateIocInput, IocKind, IocSeverity } from "@/types/edr-ioc";

const KINDS = new Set<IocKind>(["sha256", "md5", "ip", "domain", "cert_sha1"]);
const SEVS = new Set<IocSeverity>(["info", "low", "medium", "high", "critical"]);

function parseInput(raw: string): { items: CreateIocInput[]; error?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { items: [] };

  // Try JSON first.
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      const items: CreateIocInput[] = [];
      for (const row of arr) {
        if (!row || !KINDS.has(row.kind) || !row.value || !SEVS.has(row.severity)) {
          return { items: [], error: "JSON rows need kind, value, and severity." };
        }
        items.push({
          kind: row.kind,
          value: String(row.value),
          severity: row.severity,
          source: row.source,
          reference: row.reference,
          note: row.note,
        });
      }
      return { items };
    } catch (e) {
      return { items: [], error: (e as Error).message };
    }
  }

  // CSV: kind,value,severity[,source][,reference][,note]
  const items: CreateIocInput[] = [];
  const lines = trimmed.split(/\r?\n/).filter((l) => l.trim() && !l.trim().startsWith("#"));
  for (const [i, line] of lines.entries()) {
    const cells = line.split(",").map((c) => c.trim());
    const [kind, value, severity, source, reference, note] = cells;
    if (!KINDS.has(kind as IocKind)) {
      return { items: [], error: `Line ${i + 1}: unknown kind "${kind}"` };
    }
    if (!value) return { items: [], error: `Line ${i + 1}: missing value` };
    if (!SEVS.has(severity as IocSeverity)) {
      return { items: [], error: `Line ${i + 1}: unknown severity "${severity}"` };
    }
    items.push({
      kind: kind as IocKind,
      value,
      severity: severity as IocSeverity,
      source: source || undefined,
      reference: reference || undefined,
      note: note || undefined,
    });
  }
  return { items };
}

export function IocBulkImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [raw, setRaw] = useState("");
  const bulk = useBulkIocs();

  const parsed = useMemo(() => parseInput(raw), [raw]);
  const canSubmit = parsed.items.length > 0 && !parsed.error;

  const submit = async () => {
    await bulk.mutateAsync(parsed.items);
  };

  const reset = () => {
    setRaw("");
    bulk.reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk import IOCs</DialogTitle>
          <DialogDescription>
            Paste a CSV or JSON list. CSV format: <code className="text-foreground">kind,value,severity[,source,reference,note]</code>.
          </DialogDescription>
        </DialogHeader>

        {!bulk.data && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="ioc-bulk" className="text-xs">
                IOCs ({parsed.items.length} parsed{parsed.error ? " — invalid" : ""})
              </Label>
              <Textarea
                id="ioc-bulk"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder="sha256,abc...,high,manual:alice@smplify.com,VT report,suspicious mac dmg"
                rows={10}
                className="font-mono text-[11px]"
              />
              {parsed.error && (
                <p className="text-[11px] text-destructive">{parsed.error}</p>
              )}
            </div>
          </div>
        )}

        {bulk.data && (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              {bulk.data.results.filter((r) => r.ok).length} of {bulk.data.count} imported.
            </div>
            <ul className="max-h-72 overflow-y-auto divide-y divide-border rounded-md border border-border">
              {bulk.data.results.map((r) => (
                <li key={r.index} className="px-3 py-1.5 flex items-center gap-2 text-[11px]">
                  {r.ok ? (
                    <Badge variant="outline" className="bg-success-muted text-success border-success/30">
                      <Check className="h-3 w-3" />
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
                      <X className="h-3 w-3" />
                    </Badge>
                  )}
                  <span className="font-mono truncate flex-1">{r.value || `row ${r.index + 1}`}</span>
                  {!r.ok && (
                    <span className="text-destructive truncate max-w-[40%]" title={r.error}>
                      {r.error}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          {bulk.data ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button disabled={!canSubmit || bulk.isPending} onClick={submit}>
                {bulk.isPending ? "Importing…" : `Import ${parsed.items.length}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
